import { FormattedMessage, useIntl } from 'react-intl';
import { Input, Label } from '@iwoca/orion';

import { MoneyNoDecimals } from '../../../ui/moneyNoDecimals/MoneyNoDecimals';
import { intlFormatCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import messages from './ChooseLoanAmount.messages';
import styles from './ChooseLoanAmount.module.css';
import { TrackAmountSliderUsed } from '../../../../tracking/amplitude.helpers';
import { MoneyWithDecimals } from '../../../ui/moneyWithDecimals';
import { useState } from 'react';
import { Slider } from '../Slider';

type TChooseLoanAmountProps = {
    className?: string;
    setDrawdownAmount: (drawdownAmount: number) => void;
    drawdownAmount: number;
    minimumDrawdownAmount: number;
    maximumDrawdownAmount: number;
    inputTooltip?: React.ReactNode;
    isLoanBuilder?: boolean;
    setNewCalculationRequired?: (newCalculationRequired: boolean) => void;
};

export const ChooseLoanAmount = ({
    className,
    setDrawdownAmount,
    drawdownAmount,
    minimumDrawdownAmount,
    maximumDrawdownAmount,
    inputTooltip,
    isLoanBuilder = false
}: TChooseLoanAmountProps) => {
    const intl = useIntl();
    const [sliderAmount, setSliderAmount] = useState(drawdownAmount);

    const withdrawalCopy = isLoanBuilder
        ? 'borrowAmountHeader'
        : 'withdrawalAmountHeader';

    return (
        <div className={className}>
            <div className={styles.withdrawal}>
                <Label htmlFor="drawdown-amount-input">
                    <FormattedMessage
                        {...messages[withdrawalCopy]}
                        values={{
                            input: (
                                <Input
                                    id="drawdown-amount-input"
                                    data-testid="drawdown-amount-input"
                                    wrapperClassName={
                                        styles.withdrawalAmountInput
                                    }
                                    value={intlFormatCurrency({
                                        intl,
                                        value: sliderAmount
                                    })}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        const numberString = value.replace(
                                            /[^0-9]+/g,
                                            ''
                                        );
                                        const amountNumber = parseInt(
                                            numberString,
                                            10
                                        );
                                        setSliderAmount(
                                            amountNumber ||
                                                minimumDrawdownAmount
                                        );
                                    }}
                                    onBlur={() => {
                                        if (
                                            sliderAmount < minimumDrawdownAmount
                                        ) {
                                            setSliderAmount(
                                                minimumDrawdownAmount
                                            );
                                        }

                                        if (
                                            sliderAmount > maximumDrawdownAmount
                                        ) {
                                            setSliderAmount(
                                                maximumDrawdownAmount
                                            );
                                        }
                                        setDrawdownAmount(sliderAmount);
                                    }}
                                />
                            )
                        }}
                    ></FormattedMessage>
                    {inputTooltip}
                </Label>
            </div>

            <div className={styles.sliderContainer}>
                <Slider
                    minValueLabel={
                        <div data-testid="minimum-drawdown-amount">
                            <MoneyNoDecimals value={minimumDrawdownAmount} />
                        </div>
                    }
                    maxValueLabel={
                        <div
                            data-testid="maximum-drawdown-amount"
                            className={styles.sliderMax}
                        >
                            <MoneyWithDecimals value={maximumDrawdownAmount} />
                        </div>
                    }
                    id="drawdown-amount-slider"
                    name="drawdown-amount-slider"
                    sliderMax={maximumDrawdownAmount}
                    sliderMin={minimumDrawdownAmount}
                    value={sliderAmount}
                    handleChange={(value) =>
                        setSliderAmount(
                            parseInt(value) > maximumDrawdownAmount
                                ? maximumDrawdownAmount
                                : parseInt(value)
                        )
                    }
                    step={50}
                    onAfterChange={() => {
                        setDrawdownAmount(sliderAmount);
                        // @ts-expect-error: mismatched types
                        TrackAmountSliderUsed({
                            'Slider amount': sliderAmount,
                            'Loan Builder': isLoanBuilder
                        });
                    }}
                    data-action-name="Loan Amount slider"
                />
            </div>
        </div>
    );
};
